import { create } from 'zustand'
import {
  createJSONStorage,
  persist,
  type PersistOptions,
  type StateStorage
} from 'zustand/middleware'

export type ProjectTypeParameter = 'create' | 'transfert'

export interface EntryParameters {
  partnerToken?: string
  partnerDeal?: boolean
  addressId?: string
  cityId?: string
  legalStatusId?: string
  promoCode?: string
  projectType?: ProjectTypeParameter
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
}

export interface ContactSession {
  securityToken?: string
  id?: string
}

export interface SessionErrors {
  promoCode?: string
}

// Answers of the prospect that the backend does not keep, or not yet
export interface LocalAnswers {
  // Project type chosen at the project step before the Contact exists, saved with the Contact
  projectType?: ProjectTypeParameter
  // Legal status for which the prospect refused the formality: the refusal no longer holds for another one
  formalityRefusedFor?: string
  // Legal status changed in this session: the formality decided in a restored cart no longer holds
  legalStatusChanged?: boolean
  finalisationValidated?: boolean
}

export interface SessionData {
  entryParameters: EntryParameters
  contact: ContactSession
  // Session opened by a Restore session link
  restoreSession: boolean
  localAnswers: LocalAnswers
  errors: SessionErrors
  // Amount with VAT of the cart sent to payment, for the conversion event of the processing page
  transactionRevenue?: number
}

// Session persisted by version 1 of the store, before the local answers
type SessionDataV1 = Omit<SessionData, 'localAnswers' | 'transactionRevenue'>

// sessionStorage keys written one by one before the store (version 0)
const legacyKeys = [
  'contactSecurityToken',
  'contactId',
  'partnerToken',
  'hideDomideal',
  'addressId',
  'cityId',
  'legalStatusId',
  'promoCode',
  'flowType',
  'restoreSession',
  'utm_source',
  'utm_medium',
  'utm_campaign'
] as const

type LegacySessionKeys = Partial<Record<(typeof legacyKeys)[number], string>>

export const initialSession: SessionData = {
  entryParameters: {},
  contact: {},
  restoreSession: false,
  localAnswers: {},
  errors: {}
}

// Keeps the Project type of the entry link only when the navigation knows it
export const toProjectType = (value?: string | null) =>
  value === 'create' || value === 'transfert' ? value : undefined

// Converts the separate keys of a tab opened before the store into the session
const sessionFromLegacyKeys = (keys: LegacySessionKeys): SessionData => ({
  entryParameters: {
    partnerToken: keys.partnerToken,
    partnerDeal: keys.hideDomideal === '1' || undefined,
    addressId: keys.addressId,
    cityId: keys.cityId,
    legalStatusId: keys.legalStatusId,
    promoCode: keys.promoCode,
    projectType: toProjectType(keys.flowType),
    utmSource: keys.utm_source,
    utmMedium: keys.utm_medium,
    utmCampaign: keys.utm_campaign
  },
  contact: { securityToken: keys.contactSecurityToken, id: keys.contactId },
  restoreSession: keys.restoreSession === '1',
  localAnswers: {},
  errors: {}
})

// Converts the session persisted by an earlier version of the store to the current version
export const migrateSession: NonNullable<
  PersistOptions<SessionData>['migrate']
> = (persistedState, version) => {
  if (version === 0)
    return sessionFromLegacyKeys(persistedState as LegacySessionKeys)
  if (version === 1)
    return { ...initialSession, ...(persistedState as SessionDataV1) }
  return initialSession
}

// Takes the separate keys out of the tab, persisted as version 0 of the session
const takeLegacyKeys = (storage: Storage): LegacySessionKeys =>
  Object.fromEntries(
    legacyKeys.flatMap((key) => {
      const value = storage.getItem(key)
      storage.removeItem(key)
      return value === null ? [] : [[key, value]]
    })
  )

// Tab storage of the session: a tab without session yet hands its separate keys to the migration
const tabStorage = (storage: Storage): StateStorage => ({
  getItem: (name) =>
    storage.getItem(name) ??
    JSON.stringify({ state: takeLegacyKeys(storage), version: 0 }),
  setItem: (name, value) => storage.setItem(name, value),
  removeItem: (name) => storage.removeItem(name)
})

interface SessionActions {
  updateEntryParameters: (entryParameters: EntryParameters) => void
  updateContact: (contact: ContactSession) => void
  // Entry link of a new prospect: forgets the Contact and the answers of the tab
  startNewSession: (projectType?: ProjectTypeParameter) => void
  // Restore session link: the Contact of the token, without the answers of the tab
  openRestoreSession: (securityToken: string) => void
  updateLocalAnswers: (localAnswers: LocalAnswers) => void
  // Keeps the amount of the cart sent to payment, for the conversion event of the processing page
  setPromoCodeError: (promoCode?: string) => void
  setTransactionRevenue: (transactionRevenue: number) => void
}

export type SessionState = SessionData & SessionActions

// Session of the prospect in the tab: Entry parameters, Contact token, local answers, interface errors, amount
// sent to payment
export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      ...initialSession,
      updateEntryParameters: (entryParameters) =>
        set((state) => ({
          entryParameters: { ...state.entryParameters, ...entryParameters }
        })),
      updateContact: (contact) =>
        set((state) => ({ contact: { ...state.contact, ...contact } })),
      startNewSession: (projectType) =>
        set((state) => ({
          entryParameters: { ...state.entryParameters, projectType },
          contact: {},
          restoreSession: false,
          localAnswers: {}
        })),
      openRestoreSession: (securityToken) =>
        set({
          contact: { securityToken },
          restoreSession: true,
          localAnswers: {}
        }),
      updateLocalAnswers: (localAnswers) =>
        set((state) => ({
          localAnswers: { ...state.localAnswers, ...localAnswers }
        })),
      setTransactionRevenue: (transactionRevenue) =>
        set({ transactionRevenue }),
      setPromoCodeError: (promoCode) =>
        set((state) => ({ errors: { ...state.errors, promoCode } }))
    }),
    {
      name: 'session',
      version: 2,
      storage: createJSONStorage(() => tabStorage(sessionStorage)),
      partialize: ({
        entryParameters,
        contact,
        restoreSession,
        localAnswers,
        errors,
        transactionRevenue
      }) => ({
        entryParameters,
        contact,
        restoreSession,
        localAnswers,
        errors,
        transactionRevenue
      }),
      migrate: migrateSession
    }
  )
)
